// frontend/src/pages/SchoolProjects/SchoolProjectDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  School as SchoolIcon,
  Computer as ComputerIcon,
  Build as BuildIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { 
  getSchoolProjectStats, 
  getComputerParts, 
  getLowStockParts,
  getSchoolProjects,
  getSchools,
  getTechnicianAssignments,
  getPurchases
} from '../../services/api';

const SchoolProjectDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalParts: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    activeProjects: 0,
    totalProjects: 0,
    totalSchools: 0,
    totalAssignments: 0,
    totalPurchases: 0
  });
  const [lowStockItems, setLowStockItems] = useState([]);
  const [recentParts, setRecentParts] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Fetching dashboard data...');

      // Try to get dashboard stats first
      try {
        const statsResponse = await getSchoolProjectStats();
        console.log('📊 Stats response:', statsResponse);
        
        if (statsResponse) {
          // Handle different response structures
          const data = statsResponse.data || statsResponse || {};
          console.log('📈 Stats data:', data);
          
          setStats(prev => ({
            ...prev,
            totalParts: data.totalParts || data.total_parts || 0,
            totalValue: data.totalInventoryValue || data.total_value || data.total_inventory_value || 0,
            lowStockCount: data.lowStockCount || data.low_stock_count || 0,
            outOfStockCount: data.outOfStockCount || data.out_of_stock_count || 0,
            activeProjects: data.activeProjects || data.active_projects || 0,
            totalProjects: data.totalProjects || data.total_projects || 0
          }));
        }
      } catch (statsError) {
        console.warn('⚠️ Could not fetch stats, calculating from parts:', statsError);
      }

      // Fetch all other data in parallel
      const [
        lowStockResponse, 
        partsResponse, 
        projectsResponse,
        schoolsResponse,
        assignmentsResponse,
        purchasesResponse
      ] = await Promise.all([
        getLowStockParts().catch(err => {
          console.warn('Low stock fetch failed:', err);
          return { success: false, data: [] };
        }),
        getComputerParts().catch(err => {
          console.warn('Parts fetch failed:', err);
          return { success: false, data: [] };
        }),
        getSchoolProjects().catch(err => {
          console.warn('Projects fetch failed:', err);
          return { success: false, data: [] };
        }),
        getSchools().catch(err => {
          console.warn('Schools fetch failed:', err);
          return { success: false, data: [] };
        }),
        getTechnicianAssignments().catch(err => {
          console.warn('Assignments fetch failed:', err);
          return { success: false, data: [] };
        }),
        getPurchases().catch(err => {
          console.warn('Purchases fetch failed:', err);
          return { success: false, data: [] };
        })
      ]);

      // Process low stock items - FIX FOR ISSUE #5
      console.log('📦 Low stock response:', lowStockResponse);
      if (lowStockResponse && lowStockResponse.success) {
        const lowStockData = lowStockResponse.data || lowStockResponse || [];
        console.log('📦 Low stock data:', lowStockData);
        
        // Process each item to ensure part_name is available
        const processedLowStock = lowStockData.map(item => {
          // Check different possible property names for part name
          const partName = item.part_name || 
                          item.name || 
                          item.ComputerPart?.part_name || 
                          item.ComputerPart?.name ||
                          item.partName ||
                          'Unnamed Part';
          
          // Check different possible property names for part number
          const partNumber = item.part_number || 
                           item.partNumber || 
                           item.ComputerPart?.part_number ||
                           item.part_code ||
                           'N/A';
          
          // Check different possible property names for category
          const category = item.category || 
                         item.ComputerPart?.category ||
                         'Uncategorized';
          
          // Check different possible property names for quantity
          const quantity = item.quantity || 
                         item.stock || 
                         item.current_stock ||
                         item.ComputerPart?.quantity ||
                         0;
          
          // Check different possible property names for min stock
          const minStockLevel = item.min_stock_level || 
                              item.minStockLevel || 
                              item.min_stock ||
                              item.minimum_stock ||
                              item.ComputerPart?.min_stock_level ||
                              10;
          
          return {
            ...item,
            part_name: partName,
            part_number: partNumber,
            category: category,
            quantity: parseInt(quantity) || 0,
            min_stock_level: parseInt(minStockLevel) || 10
          };
        });
        
        setLowStockItems(processedLowStock);
        
        // Update low stock count if not set by stats API
        if (processedLowStock.length > 0 && stats.lowStockCount === 0) {
          setStats(prev => ({
            ...prev,
            lowStockCount: processedLowStock.length
          }));
        }
      } else {
        setLowStockItems([]);
      }

      // Process recent parts
      if (partsResponse && partsResponse.success) {
        const allParts = partsResponse.data || partsResponse || [];
        console.log('🔧 All parts:', allParts.length);
        
        // Process each part to ensure consistent property names
        const processedParts = allParts.map(part => {
          const partName = part.part_name || part.name || 'Unnamed Part';
          const partNumber = part.part_number || part.partNumber || part.code || 'N/A';
          const purchasePrice = parseFloat(part.purchase_price || part.price || part.cost || 0);
          const quantity = parseInt(part.quantity || part.stock || part.current_stock || 0);
          const minStockLevel = parseInt(part.min_stock_level || part.minStockLevel || 10);
          
          return {
            ...part,
            part_name: partName,
            part_number: partNumber,
            purchase_price: purchasePrice,
            quantity: quantity,
            min_stock_level: minStockLevel
          };
        });
        
        // Sort by created date and take first 5
        const sortedParts = [...processedParts].sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || a.date_added || 0);
          const dateB = new Date(b.created_at || b.createdAt || b.date_added || 0);
          return dateB - dateA;
        }).slice(0, 5);
        
        setRecentParts(sortedParts);
        
        // Calculate total value and update stats if not set by API
        if (processedParts.length > 0) {
          const totalValue = processedParts.reduce((sum, part) => {
            return sum + (part.purchase_price * part.quantity);
          }, 0);
          
          const outOfStockCount = processedParts.filter(part => part.quantity === 0).length;
          
          setStats(prev => ({
            ...prev,
            totalParts: processedParts.length,
            totalValue: prev.totalValue === 0 ? totalValue : prev.totalValue,
            outOfStockCount: prev.outOfStockCount === 0 ? outOfStockCount : prev.outOfStockCount
          }));
        }
      } else {
        setRecentParts([]);
      }

      // Process recent projects - FIX FOR ISSUES #1 & #4
      if (projectsResponse && projectsResponse.success) {
        const allProjects = projectsResponse.data || projectsResponse || [];
        console.log('🏫 All projects:', allProjects.length);
        
        // Process each project to ensure consistent property names
        const processedProjects = allProjects.map(project => {
          const projectName = project.project_name || project.name || 'Unnamed Project';
          const status = project.status || 'pending';
          const isActive = status === 'in_progress' || status === 'in-progress' || status === 'active';
          
          return {
            ...project,
            project_name: projectName,
            status: status,
            isActive: isActive
          };
        });
        
        // Sort by created date and take first 3
        const sortedProjects = [...processedProjects].sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || a.start_date || 0);
          const dateB = new Date(b.created_at || b.createdAt || b.start_date || 0);
          return dateB - dateA;
        }).slice(0, 3);
        
        setRecentProjects(sortedProjects);
        
        // Calculate active projects and total projects
        const activeProjectsCount = processedProjects.filter(p => p.isActive).length;
        const totalProjectsCount = processedProjects.length;
        
        setStats(prev => ({
          ...prev,
          activeProjects: prev.activeProjects === 0 ? activeProjectsCount : prev.activeProjects,
          totalProjects: prev.totalProjects === 0 ? totalProjectsCount : prev.totalProjects
        }));
      } else {
        setRecentProjects([]);
      }

      // Update other stats
      if (schoolsResponse && schoolsResponse.success) {
        const schoolsData = schoolsResponse.data || schoolsResponse || [];
        setStats(prev => ({
          ...prev,
          totalSchools: schoolsData.length
        }));
      }

      if (assignmentsResponse && assignmentsResponse.success) {
        const assignmentsData = assignmentsResponse.data || assignmentsResponse || [];
        setStats(prev => ({
          ...prev,
          totalAssignments: assignmentsData.length
        }));
      }

      if (purchasesResponse && purchasesResponse.success) {
        const purchasesData = purchasesResponse.data || purchasesResponse || [];
        setStats(prev => ({
          ...prev,
          totalPurchases: purchasesData.length
        }));
      }

      console.log('✅ Final stats:', stats);

    } catch (err) {
      console.error('❌ Dashboard error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-LK', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      pending: 'info',
      'in_progress': 'warning',
      'in-progress': 'warning',
      in_progress: 'warning',
      active: 'warning',
      completed: 'success',
      delivered: 'secondary',
      installed: 'primary',
      cancelled: 'error'
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Pending',
      'in_progress': 'In Progress',
      'in-progress': 'In Progress',
      in_progress: 'In Progress',
      active: 'Active',
      completed: 'Completed',
      delivered: 'Delivered',
      installed: 'Installed',
      cancelled: 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const handleViewPart = (partId) => {
    navigate(`/school-projects/inventory`);
  };

  const handleViewProject = (projectId) => {
    navigate(`/school-projects/projects/${projectId}`);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
        <Typography sx={{ textAlign: 'center', mt: 2 }}>Loading dashboard data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
          School Projects Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboardData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/school-projects/inventory')}
            sx={{ ml: 1 }}
          >
            Add Part
          </Button>
        </Box>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Debug Info - Can be removed after fixing */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Data Status: Parts: {stats.totalParts} | Projects: {stats.totalProjects} | Low Stock: {stats.lowStockCount}
        </Typography>
      </Alert>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/inventory')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Parts
                  </Typography>
                  <Typography variant="h4">{stats.totalParts}</Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {recentParts.length} recently added
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/inventory')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Value
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(stats.totalValue)}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, color: '#2ecc71', opacity: 0.8 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Inventory worth
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/inventory')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Low Stock
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.lowStockCount}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: '#f39c12', opacity: 0.8 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {lowStockItems.length} items need attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/projects')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Projects
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.activeProjects}
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: '#9b59b6', opacity: 0.8 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {stats.totalProjects} total projects
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/schools')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Schools
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalSchools}
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Partner schools
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Low Stock Items - FIXED FOR ISSUE #5 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  Low Stock Items ({lowStockItems.length})
                </Typography>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/school-projects/inventory')}
                >
                  View All
                </Button>
              </Box>
              
              {lowStockItems.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                  No low stock items found. All parts are well-stocked.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Part Name</strong></TableCell>
                        <TableCell><strong>Part Number</strong></TableCell>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell align="right"><strong>Stock</strong></TableCell>
                        <TableCell align="right"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lowStockItems.slice(0, 5).map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.part_name || 'Unnamed Part'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {item.part_number || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={item.category || 'Uncategorized'} 
                              size="small"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              color={item.quantity === 0 ? 'error.main' : 'warning.main'}
                              sx={{ fontWeight: 'bold' }}
                            >
                              {item.quantity || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Min: {item.min_stock_level || 10}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small"
                                onClick={() => handleViewPart(item.id)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Projects - FIXED FOR ISSUES #1 & #4 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon color="primary" />
                  Recent Projects ({recentProjects.length})
                </Typography>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/school-projects/projects')}
                >
                  View All
                </Button>
              </Box>
              
              {recentProjects.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                  No projects found. Click "Manage Projects" to create a new project.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Project Name</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentProjects.map((project, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {project.project_name || 'Unnamed Project'}
                            </Typography>
                            {project.School && (
                              <Typography variant="caption" color="text.secondary">
                                {project.School.school_name || project.School.name || 'N/A'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getStatusText(project.status)} 
                              size="small"
                              color={getStatusColor(project.status)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small"
                                onClick={() => handleViewProject(project.id)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Parts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ComputerIcon color="primary" />
                  Recent Parts ({recentParts.length})
                </Typography>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/school-projects/inventory')}
                >
                  View All
                </Button>
              </Box>
              
              {recentParts.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                  No parts found. Click "Add Part" to add new computer parts.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Part Name</strong></TableCell>
                        <TableCell><strong>Part Number</strong></TableCell>
                        <TableCell align="right"><strong>Price</strong></TableCell>
                        <TableCell align="right"><strong>Stock</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentParts.map((part, index) => (
                        <TableRow key={index} hover onClick={() => handleViewPart(part.id)} sx={{ cursor: 'pointer' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {part.part_name || 'Unnamed Part'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {part.part_number || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(part.purchase_price)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%',
                                backgroundColor: part.quantity === 0 ? '#e74c3c' : 
                                               part.quantity <= (part.min_stock_level || 10) ? '#f39c12' : '#2ecc71'
                              }} />
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {part.quantity || 0}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6} md={3}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                        boxShadow: 2
                      } 
                    }}
                    onClick={() => navigate('/school-projects/inventory')}
                  >
                    <InventoryIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" fontWeight="medium">Manage Inventory</Typography>
                    <Typography variant="caption" color="text.secondary">
                      View & manage parts
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                        boxShadow: 2
                      } 
                    }}
                    onClick={() => navigate('/school-projects/projects')}
                  >
                    <SchoolIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" fontWeight="medium">Manage Projects</Typography>
                    <Typography variant="caption" color="text.secondary">
                      View school projects
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                        boxShadow: 2
                      } 
                    }}
                    onClick={() => navigate('/school-projects/technician-parts/issue')}
                  >
                    <AssignmentIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" fontWeight="medium">Issue Parts</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Assign to technicians
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                        boxShadow: 2
                      } 
                    }}
                    onClick={() => navigate('/school-projects/purchases')}
                  >
                    <ReceiptIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" fontWeight="medium">Record Purchase</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add new parts
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/technician-parts/assignments')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 40, color: '#3498db', mb: 1 }} />
              <Typography variant="h4">{stats.totalAssignments}</Typography>
              <Typography variant="body2" color="text.secondary">Total Assignments</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/purchases')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReceiptIcon sx={{ fontSize: 40, color: '#2ecc71', mb: 1 }} />
              <Typography variant="h4">{stats.totalPurchases}</Typography>
              <Typography variant="body2" color="text.secondary">Total Purchases</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/projects')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ComputerIcon sx={{ fontSize: 40, color: '#9b59b6', mb: 1 }} />
              <Typography variant="h4">{stats.totalProjects}</Typography>
              <Typography variant="body2" color="text.secondary">Total Projects</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/school-projects/reports')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: '#f39c12', mb: 1 }} />
              <Typography variant="h4">
                {stats.totalProjects > 0 ? Math.round((stats.activeProjects / stats.totalProjects) * 100) : 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">Project Progress</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchoolProjectDashboard;